import React, { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import { findHiddenCursor, findHiddenTrail } from "../lib/hiddenCosmetics";

/**
 * EventRewardModal — single shared popup used by every glitch event.
 * Props:
 *  open: boolean
 *  reward: { kind: "cursor"|"trail"|"card", item_id, item_name, card_name?, source: "blackout"|"royale" }
 *  onAdd:   () => Promise<void>   // "Add to my collection" handler
 *  onClose: () => void
 *  busy: boolean                  // disables the CTA while the claim is in-flight
 *  alreadyAdded: boolean          // post-claim state — swap CTA to dismiss
 */
export default function EventRewardModal({ open, reward, onAdd, onClose, busy, alreadyAdded }) {
  // Live-preview the cursor on a target div so the user can hover it.
  const previewRef = useRef(null);
  const cursor = useMemo(() => {
    if (!reward || reward.kind !== "cursor") return null;
    return findHiddenCursor(reward.item_id);
  }, [reward]);
  const trail = useMemo(() => {
    if (!reward || reward.kind !== "trail") return null;
    return findHiddenTrail(reward.item_id);
  }, [reward]);
  useEffect(() => {
    if (!previewRef.current) return;
    if (cursor?.css) previewRef.current.style.cursor = cursor.css;
    else previewRef.current.style.cursor = "auto";
  }, [cursor]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !reward) return null;

  const isCard = reward.kind === "card";
  const itemName = reward.item_name || reward.card_name || "Mystery Reward";

  // Event flavor — keeps the brutalist vibe without spoiling what the event does.
  const sourceTag = {
    blackout: "// SIGNAL · BLACKOUT //",
    royale: "// SIGNAL · ROYALE //",
  }[reward.source] || "// SIGNAL //";

  return (
    <div
      data-testid="event-reward-modal"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-[420px] bg-black border-2 border-white shadow-[0_0_56px_-8px_rgba(255,255,255,0.55)] font-mono"
      >
        <button
          type="button"
          data-testid="event-reward-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 text-white/60 hover:text-white p-1"
        >
          <X size={16} />
        </button>

        <div className="px-4 pt-4 pb-2 text-[9px] tracking-[0.35em] uppercase text-white/55">
          {sourceTag}
        </div>

        {/* Preview tile — cursor lets the user hover-feel it; trail/card show a glyph */}
        <div
          ref={previewRef}
          className="mx-4 mb-3 border border-white/15 bg-[#0a0a0a] flex items-center justify-center"
          style={{ height: 180 }}
        >
          {cursor ? (
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 text-center px-4 leading-relaxed">
              HOVER ANYWHERE<br/>INSIDE THIS BOX<br/>TO FEEL IT
            </div>
          ) : trail ? (
            <TrailPreview recipe={trail.recipe} />
          ) : isCard ? (
            <div className="text-center px-6">
              <div className="text-[40px] leading-none">◈</div>
              <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-white/55">DIVINE VAULT CARD</div>
            </div>
          ) : (
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">NO PREVIEW</div>
          )}
        </div>

        <div className="px-4 pb-4 space-y-3">
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">YOU FOUND</div>
            <div className="mt-1 text-[20px] text-white leading-tight">{itemName}</div>
            <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-white/55">
              {isCard ? "DIVINE · VAULT CARD" : reward.kind === "cursor" ? "HIDDEN CURSOR" : "HIDDEN TRAIL"}
            </div>
          </div>

          {alreadyAdded ? (
            <button
              type="button"
              data-testid="event-reward-dismiss"
              onClick={onClose}
              className="w-full px-3 py-3 text-[10px] tracking-[0.3em] uppercase border-2 border-white/30 text-white/70 bg-transparent hover:border-white hover:text-white transition-colors"
            >
              ADDED · DISMISS
            </button>
          ) : (
            <button
              type="button"
              data-testid="event-reward-add"
              disabled={busy}
              onClick={onAdd}
              className="w-full px-3 py-3 text-[10px] tracking-[0.3em] uppercase border-2 bg-white border-white text-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              {busy ? "ADDING…" : "ADD TO MY COLLECTION"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TrailPreview({ recipe }) {
  // A static peek of the trail glyph + color so the user can see what
  // they're getting without running the full particle engine.
  if (!recipe) return null;
  const glyph = recipe.glyph || (recipe.kind === "blob" ? "●" : recipe.kind === "dots" ? "···" : "◌");
  return (
    <div className="text-center" style={{ color: recipe.color || "#fff" }}>
      <div style={{ fontSize: 52, lineHeight: 1, textShadow: `0 0 18px ${recipe.color || "#fff"}` }}>
        {glyph}
      </div>
      <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-white/55">CURSOR TRAIL</div>
    </div>
  );
}
